import { H } from "@jfrk/react-heading-levels";
import * as defaultStyles from "@whitespace/gatsby-plugin-smorgasbord-aptus-booking/src/components/BookModal.module.css";
import Timeslot from "@whitespace/gatsby-plugin-smorgasbord-aptus-booking/src/components/Timeslot";
import AptusContext from "@whitespace/gatsby-plugin-smorgasbord-aptus-booking/src/contexts/aptusContext";
import { apartmentContext } from "@whitespace/gatsby-plugin-smorgasbord-fastapi";
import {
  ErrorMessage,
  Loading,
} from "@whitespace/gatsby-theme-smorgasbord/src/components";
import clsx from "clsx";
import { parseISO } from "date-fns";
import PropTypes from "prop-types";
import React, { useState, useContext, useRef, useEffect } from "react";

import { createVerifiedBooking } from "../../../aptus-booking/booking";

import ConfirmationScreen from "./ConfirmationScreen";

BookModal.propTypes = {
  styles: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  day: PropTypes.object.isRequired,
};

export default function BookModal({
  styles = defaultStyles,
  className,
  day,
  ...restProps
}) {
  const { apiPath, bookedPass, setBookedPass, setBookings, groups } =
    useContext(AptusContext);
  const { selectedApartment } = useContext(apartmentContext);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const pending = useRef(false);
  const active = useRef(true);
  const selection = `${selectedApartment?.id}/${day.date}/${day.groupId}`;
  const currentSelection = useRef(selection);
  currentSelection.current = selection;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const groupName = groups.find(
    (group) => String(group.id) === String(day.groupId),
  )?.name;
  const date = parseISO(day.date).toLocaleDateString("sv", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const confirmation =
    bookedPass?.apartmentId === selectedApartment?.id &&
    bookedPass?.dayDate === day.date &&
    bookedPass?.groupId === day.groupId
      ? bookedPass
      : null;

  async function handleCreateBooking(pass) {
    if (pending.current || !selectedApartment?.id) return;
    pending.current = true;
    setError(null);
    setLoading(true);
    const apartmentId = selectedApartment.id;
    try {
      const { booking, bookings } = await createVerifiedBooking({
        apiPath,
        apartmentId,
        startTime: `${day.date}T${pass.StartTime}`,
        length: pass.LengthMinutes,
        groupId: day.groupId,
        groupName,
      });
      if (!active.current || currentSelection.current !== selection) return;
      setBookedPass({
        ...pass,
        booking,
        apartmentId,
        dayDate: day.date,
        groupId: day.groupId,
      });
      setBookings(bookings);
    } catch (error) {
      if (active.current && currentSelection.current === selection) {
        setError(error.message);
      }
    } finally {
      pending.current = false;
      if (active.current) setLoading(false);
    }
  }

  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;

  return (
    <div className={clsx(styles.component, className)} {...restProps}>
      <H className={clsx(styles.headingDate, className)}>
        {date.charAt(0).toLocaleUpperCase("sv") + date.slice(1)}
      </H>
      <H className={clsx(styles.headingGroupName, className)}>{groupName}</H>
      {confirmation ? (
        <ConfirmationScreen confirmation={confirmation} />
      ) : (
        <ul className={clsx(styles.list, className)}>
          {day.passes.map((pass, index) => (
            <li key={index} className={clsx(styles.listItem, className)}>
              <Timeslot
                pass={pass}
                handleCreateBooking={() => handleCreateBooking(pass)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
